import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import {
  fetchUserAuditTrial,
  fetchUserAuditTrials,
} from 'shared/api/services/userAuditTrialService';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';

const OUTCOME_OPTIONS = ['', 'SUCCESS', 'FAILURE'];
const ACTIVITY_OPTIONS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'ADD', 'MOD', 'DEL'];
const DEFAULT_PAGE_SIZE = 10;

const displayValue = (value) =>
  value != null && String(value).trim() !== '' ? String(value) : '—';

const DetailField = ({ label, value, mono = false, fullWidth = false }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: 'text.secondary',
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        lineHeight: 1.45,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        fontFamily: mono
          ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
          : 'inherit',
        fontSize: mono ? '0.8rem' : '0.9rem',
      }}
    >
      {displayValue(value)}
    </Typography>
  </Grid>
);

const DetailSection = ({ title, children }) => (
  <Box
    sx={{
      mb: 2,
      p: 1.75,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: (theme) =>
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.03)
          : alpha(theme.palette.common.black, 0.02),
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 800,
        mb: 1.25,
        letterSpacing: '0.02em',
        color: 'text.primary',
      }}
    >
      {title}
    </Typography>
    <Grid container spacing={1.75}>
      {children}
    </Grid>
  </Box>
);

const UserAuditTrialPage = () => {
  const theme = useTheme();
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.user-audit-trial');

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [outcome, setOutcome] = useState('');
  const [activity, setActivity] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUserAuditTrials({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        outcome: outcome || undefined,
        activity: activity || undefined,
        q: appliedSearch.trim() || undefined,
      });
      setRows(result.rows);
      setTotalRecords(result.totalRecords);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || 'Failed to load audit trials';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load audit trials');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    outcome,
    activity,
    appliedSearch,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchInput.trim();
      setAppliedSearch((prev) => {
        if (prev === next) return prev;
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return next;
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const clearFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setOutcome('');
    setActivity('');
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  const openDetail = async (id) => {
    try {
      const data = await fetchUserAuditTrial(id);
      setDetail(data);
      setDetailOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load audit detail');
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'activityAt',
        headerName: 'Date / time (EAT)',
        flex: 1.1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'username',
        headerName: 'User',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) =>
          params?.row?.username || params?.row?.userEmail || params?.row?.userId || '—',
      },
      { field: 'userEmail', headerName: 'Email', flex: 1.2, minWidth: 160 },
      { field: 'userRole', headerName: 'Role', width: 110 },
      { field: 'activity', headerName: 'Activity', width: 100 },
      {
        field: 'requestUri',
        headerName: 'Request / path',
        flex: 1.4,
        minWidth: 180,
        valueGetter: (params) => {
          const row = params?.row || {};
          const method = row.requestMethod || '';
          const uri = row.requestUri || row.affectedEntity || '';
          return `${method} ${uri}`.trim() || '—';
        },
      },
      {
        field: 'affectedEntity',
        headerName: 'Affected type',
        flex: 0.8,
        minWidth: 110,
        valueGetter: (params) => {
          const row = params?.row || {};
          return row.affectedEntity && row.affectedEntity !== 'HTTP'
            ? row.affectedEntity
            : '—';
        },
      },
      {
        field: 'affectedRecordName',
        headerName: 'Affected record',
        flex: 1.2,
        minWidth: 150,
        valueGetter: (params) => {
          const row = params?.row || {};
          const name = (row.affectedRecordName || '').trim();
          const id = (row.affectedRecordId || '').trim();
          if (name && id) return `${name} (#${id})`;
          if (name) return name;
          return id || '—';
        },
      },
      {
        field: 'changesMade',
        headerName: 'What changed (old → new)',
        flex: 2.2,
        minWidth: 220,
      },
      {
        field: 'outcome',
        headerName: 'Outcome',
        width: 110,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value || '—'}
            color={String(params.value).toUpperCase() === 'FAILURE' ? 'error' : 'success'}
            variant="outlined"
          />
        ),
      },
      { field: 'source', headerName: 'Source', width: 100 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            color="secondary"
            sx={{ textTransform: 'none' }}
            onClick={() => openDetail(params.row.id)}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <Box m="20px">
      <Header
        title="User audit trail"
        subtitle="Field-level history — what each value was changed from and to"
      />

      <Box mt={2} mb={1.5}>
        <TextField
          size="small"
          placeholder="Search users / path…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            mb: 1.5,
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: 360,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#fff',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearchInput('')}
                  edge="end"
                >
                  <ClearOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <TableContainer
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflowX: 'auto',
          }}
        >
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 640 }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'middle', width: '32%', border: 0 }}>
                  <SearchableSelect
                    size="small"
                    label="Activity"
                    value={activity}
                    allowEmpty={false}
                    onChange={(next) => {
                      setActivity(next);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    options={ACTIVITY_OPTIONS.map((opt) => ({
                      value: opt,
                      label: opt || 'All activities',
                    }))}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'middle', width: '32%', border: 0 }}>
                  <SearchableSelect
                    size="small"
                    label="Outcome"
                    value={outcome}
                    allowEmpty={false}
                    onChange={(next) => {
                      setOutcome(next);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    options={OUTCOME_OPTIONS.map((opt) => ({
                      value: opt,
                      label: opt || 'All outcomes',
                    }))}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'middle', width: '18%', border: 0 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<RefreshIcon />}
                    onClick={load}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Refresh
                  </Button>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'middle', width: '18%', border: 0 }}>
                  <Button
                    variant="text"
                    onClick={clearFilters}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Clear
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box position="relative" sx={{ opacity: loading ? 0.4 : 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          pagination
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['desc']}
          initialState={{
            sorting: { sortModel: [{ field: 'activityAt', sort: 'desc' }] },
          }}
          rowCount={totalRecords}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: buildGridToolbarExportProps({
              title: 'User audit trail',
              tableId: 'platform.user-audit-trail',
            }),
          }}
          localeText={{
            noRowsLabel:
              'No field changes yet. Edit a record in the app, then refresh this page.',
          }}
        />
        {loading ? (
          <Box position="absolute" top="40%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
            <LoadingSpinner message="Loading audit trials…" />
          </Box>
        ) : null}
      </Box>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: (t) =>
              t.palette.mode === 'dark'
                ? alpha(t.palette.secondary.main, 0.12)
                : alpha(t.palette.secondary.main, 0.08),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box minWidth={0} flex={1}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.25}>
              Activity detail
            </Typography>
            {detail ? (
              <Box mt={1} display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
                <Chip
                  size="small"
                  label={displayValue(detail.activity)}
                  color="secondary"
                  variant="filled"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={displayValue(detail.outcome)}
                  color={
                    String(detail.outcome || '').toUpperCase() === 'FAILURE' ? 'error' : 'success'
                  }
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                {detail.source ? (
                  <Chip
                    size="small"
                    label={detail.source}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                ) : null}
                <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 0.5 } }}>
                  {displayValue(detail.activityAt)}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.25 }}>
          {detail ? (
            <Box>
              <DetailSection title="Who">
                <DetailField label="Username" value={detail.username} />
                <DetailField label="Email" value={detail.userEmail} />
                <DetailField label="User ID" value={detail.userId} mono />
                <DetailField label="Role" value={detail.userRole} />
              </DetailSection>

              <DetailSection title="What changed">
                <DetailField label="Affected entity" value={detail.affectedEntity} />
                <DetailField
                  label="Affected record"
                  value={
                    detail.affectedRecordName
                      ? detail.affectedRecordId
                        ? `${detail.affectedRecordName} (#${detail.affectedRecordId})`
                        : detail.affectedRecordName
                      : detail.affectedRecordId
                  }
                />
                <DetailField label="Activity" value={detail.activity} />
                <DetailField label="Outcome" value={detail.outcome} />
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    Field changes (old → new)
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: (t) => alpha(t.palette.secondary.main, 0.35),
                      bgcolor: (t) =>
                        t.palette.mode === 'dark'
                          ? alpha(t.palette.common.black, 0.35)
                          : alpha(t.palette.secondary.main, 0.06),
                      maxHeight: 260,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.8rem',
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {displayValue(detail.changesMade)}
                    </Typography>
                  </Box>
                </Grid>
              </DetailSection>

              <DetailSection title="Request">
                <DetailField
                  label="Method & path"
                  value={`${detail.requestMethod || ''} ${detail.requestUri || ''}`.trim()}
                  mono
                  fullWidth
                />
                <DetailField label="Source IP" value={detail.sourceIp} mono />
                <DetailField label="HTTP status" value={detail.httpStatus} />
                <DetailField label="Tenant" value={detail.tenantId} mono />
                <DetailField label="Audit ID" value={detail.id} mono />
                <DetailField label="User agent" value={detail.userAgent} fullWidth />
              </DetailSection>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <LoadingSpinner message="Loading detail…" />
            </Box>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => setDetailOpen(false)}
            variant="contained"
            color="secondary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAuditTrialPage;
